import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiPencilSquare, HiArrowsRightLeft, HiPlusCircle, HiMinusCircle, HiArrowRight } from 'react-icons/hi2';
import { formatShortDate } from '../../utils/plan_changes';
import { formatDurationMin } from '../../utils/duration';

const OP_META = {
  patch: { Icon: HiPencilSquare, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  move: { Icon: HiArrowsRightLeft, cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  add: { Icon: HiPlusCircle, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  remove: { Icon: HiMinusCircle, cls: 'bg-red-50 text-red-700 border-red-200' }
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

function Metric({ before, after, format, highlight }) {
  const b = num(before);
  const a = num(after);
  if (b == null && a == null) return null;
  const changed = highlight && b !== a;
  if (!changed) {
    return (
      <span className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-600 tabular-nums">{format(a ?? b)}</span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-900 tabular-nums inline-flex items-center gap-1">
      {b != null ? <span className="line-through opacity-60">{format(b)}</span> : null}
      {b != null ? <HiArrowRight className="w-3 h-3" /> : null}
      <span className="font-black">{a != null ? format(a) : '—'}</span>
    </span>
  );
}

export default function CoachChangeList({ preview, className = '' }) {
  const { t, i18n } = useTranslation();
  const items = Array.isArray(preview) ? preview.filter(Boolean) : [];
  if (items.length === 0) return null;

  const typeLabel = (type) => (type ? t(`workout.types.${type}`, { defaultValue: String(type).replace(/_/g, ' ') }) : null);
  const km = (v) => `${Math.round(v * 10) / 10} km`;
  const min = (v) => formatDurationMin(v) || `${v} min`;
  const elev = (v) => `+${Math.round(v)} m`;

  return (
    <ul className={`space-y-2 ${className}`}>
      {items.map((item, idx) => {
        const op = OP_META[item.op] ? item.op : 'patch';
        const { Icon, cls } = OP_META[op];
        const before = item.before || null;
        const after = item.after || null;
        const main = after || before || {};
        const isRemove = op === 'remove';
        const isPatchOrMove = op === 'patch' || op === 'move';
        const dateChanged = Boolean(before?.date && after?.date && before.date !== after.date);
        const titleChanged = Boolean(op === 'patch' && before?.title && after?.title && before.title !== after.title);
        const typeChanged = Boolean(before?.type && after?.type && before.type !== after.type);

        return (
          <li key={idx} className="rounded-xl border border-gray-100 bg-white p-3">
            <div className="flex items-start gap-2">
              <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wide ${cls}`}>
                <Icon className="w-3.5 h-3.5" />
                {t(`coach_options.op.${op}`)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold text-gray-500 flex flex-wrap items-center gap-1">
                  {dateChanged ? (
                    <>
                      <span className="line-through opacity-70">{formatShortDate(before.date, i18n.language)}</span>
                      <HiArrowRight className="w-3 h-3" />
                      <span className="text-sky-700 font-black">{formatShortDate(after.date, i18n.language)}</span>
                    </>
                  ) : (
                    <span>{formatShortDate(main.date, i18n.language)}</span>
                  )}
                  {main.type ? (
                    <>
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      {typeChanged ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="line-through opacity-70">{typeLabel(before.type)}</span>
                          <HiArrowRight className="w-3 h-3" />
                          <span className="text-amber-800 font-black">{typeLabel(after.type)}</span>
                        </span>
                      ) : (
                        <span>{typeLabel(main.type)}</span>
                      )}
                    </>
                  ) : null}
                </div>
                <div className={`text-sm font-black leading-snug mt-0.5 ${isRemove ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  {titleChanged ? (
                    <>
                      <span className="line-through text-gray-400 font-bold mr-1">{before.title}</span>
                      {after.title}
                    </>
                  ) : (
                    main.title || t('coach_options.untitled_session')
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-bold">
                  <Metric before={isPatchOrMove ? before?.distance_km : null} after={isRemove ? before?.distance_km : after?.distance_km} format={km} highlight={isPatchOrMove} />
                  <Metric before={isPatchOrMove ? before?.duration_min : null} after={isRemove ? before?.duration_min : after?.duration_min} format={min} highlight={isPatchOrMove} />
                  <Metric before={isPatchOrMove ? before?.altitude_gain_m : null} after={isRemove ? before?.altitude_gain_m : after?.altitude_gain_m} format={elev} highlight={isPatchOrMove} />
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
